import qs from 'query-string'

import { updateParamsInUrl } from '../utils/Urls'

let params = {}
let handleError = null
const POST_SUMMARY = 'postSummary'
const requestNameToAbortControllers = {
  [POST_SUMMARY]: []
}

function init({ locale, errorHandler, url, paymentServiceUrl }) {
  const api = `${url}/api`
  const paymentServiceApi = `${paymentServiceUrl}/api`
  params = { locale, api, paymentServiceApi }
  handleError = errorHandler
}

function deactivate() {
  params = {}
  handleError = null
}

async function fetchWithErrorHandling(resource, init, errorHandler = handleError, throwGlobalError = true) {
  try {
    const response = await fetch(resource, init)

    // eslint-disable-next-line no-throw-literal
    if (response.status >= 400) throw { status: response.status, url: response.url }
    if (response.status === 204) return Promise.resolve()
    return response.json()
  } catch (error) {
    if (errorHandler) errorHandler(error)
    if (throwGlobalError) throw error
  }
}

function getClientBySsoToken(ssoToken, handleError) {
  return fetchWithErrorHandling(`${params.api}/sso/${ssoToken}/client`, undefined, handleError, false)
}

function getAvailableSerialTickets(queryParams) {
  return fetchWithErrorHandling(
    `${params.api}/serial-tickets?${qs.stringify({ locale: params.locale, ...queryParams })}`
  )
}

function getVoucherInfo(code) {
  return fetchWithErrorHandling(`${params.api}/vouchers/${code}`)
}

function getUserCoupons(queryParams) {
  return fetchWithErrorHandling(`${params.api}/coupons?${qs.stringify({ ...queryParams })}`)
}

function getReservation(token) {
  return fetchWithErrorHandling(`${params.api}/reservations/${token}?${qs.stringify({ locale: params.locale })}`)
}

function getCompany(companyId) {
  return fetchWithErrorHandling(`${params.api}/companies/${companyId}`)
}

function getAgent(agentId, companyId) {
  return fetchWithErrorHandling(`${params.api}/companies/${companyId}/agents/${agentId}`)
}

function getChannelMerchant() {
  return fetchWithErrorHandling(
    `${params.api}/payments/merchant?${qs.stringify({ locale: params.locale })}`,
    undefined,
    () => {}
  )
}

async function postSummary(postParams) {
  try {
    const abortController = createAbortController(POST_SUMMARY)
    const response = await fetch(`${params.api}/reservations/summary`, {
      signal: abortController.signal,
      mode: 'cors',
      method: 'post',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8'
      },
      body: JSON.stringify(postParams)
    })
    return response.json()
  } catch (ex) {
    if (ex.name === 'AbortError') return
    throw ex
  }
}

function abortPostSummary() {
  abortRequest(POST_SUMMARY)
}

function createAbortController(requestName) {
  abortRequest(requestName)
  const abortController = new AbortController()
  requestNameToAbortControllers[requestName].push(abortController)
  return abortController
}

function abortRequest(requestName) {
  const latestAbortController = requestNameToAbortControllers[requestName].pop()
  if (latestAbortController) latestAbortController.abort()
}

async function getMessages(response) {
  const SYSTEM_ERROR_CODE = -1
  try {
    const { errors } = await response.json()
    return errors.filter(({ code }) => code !== SYSTEM_ERROR_CODE).map(({ message }) => message)
  } catch (error) {
    return null
  }
}

async function confirmReservation(requestData) {
  try {
    const response = await fetch(`${params.api}/reservations/confirm?${qs.stringify({ locale: params.locale })}`, {
      mode: 'cors',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    })

    if (response.status >= 400) {
      const messages = await getMessages(response)
      if (messages?.length > 0) return { error: true, messages }

      // eslint-disable-next-line no-throw-literal
      throw { status: response.status, url: response.url }
    }
    return response.json()
  } catch (error) {
    if (handleError) handleError(error)
    throw error
  }
}

function redirectToConfirmationPage(confirmResponse) {
  updateHistoryWithPaymentCancelledUrl()

  const hashParameters = { '/': '', afterChange: '' }
  const requestParameters = {
    ...qs.parseUrl(confirmResponse.url).query,
    ...confirmResponse.parameters
  }
  window.location.href = updateParamsInUrl(confirmResponse.url, requestParameters, hashParameters)
}

function redirectToPaymentServicePage(confirmResponse) {
  updateHistoryWithPaymentCancelledUrl()

  window.location.href = updateParamsInUrl(confirmResponse.url, confirmResponse.parameters)
}

function redirectToPaymentPage(confirmResponse) {
  updateHistoryWithPaymentCancelledUrl()

  submitPaymentForm()

  function submitPaymentForm() {
    const formMethod = 'post'
    const form = createForm(formMethod, confirmResponse.url)

    Object.entries(confirmResponse.parameters).forEach(([name, value]) => addInputToForm(name, value, form))

    document.body.appendChild(form)

    form.submit()
  }

  function createForm(method, action) {
    const form = document.createElement('form')
    form.setAttribute('method', method)
    form.setAttribute('action', action)
    form.setAttribute('id', 'redirect-form')
    form.setAttribute('style', 'display: none')
    return form
  }

  function addInputToForm(name, value, form) {
    const input = document.createElement('input')
    input.setAttribute('name', name)
    input.setAttribute('value', value)
    form.appendChild(input)
  }
}

function updateHistoryWithPaymentCancelledUrl() {
  const { location } = window
  const paymentCancelledUrl = updateParamsInUrl(location.href, {
    ...qs.parse(location.search),
    backendError: 'paymentCancelled'
  })
  window.history.replaceState({}, '', paymentCancelledUrl)
}

function getAvailablePaymentTypes() {
  return fetchWithErrorHandling(`${params.api}/v3/payments?${qs.stringify({ locale: params.locale })}`)
}

function createPdfLink(resToken) {
  return `${params.api}/reservations/${resToken}/pdf`
}

export default {
  abortPostSummary,
  confirmReservation,
  createPdfLink,
  deactivate,
  getAgent,
  getAvailablePaymentTypes,
  getAvailableSerialTickets,
  getClientBySsoToken,
  getChannelMerchant,
  getCompany,
  getReservation,
  getUserCoupons,
  getVoucherInfo,
  init,
  postSummary,
  redirectToConfirmationPage,
  redirectToPaymentServicePage,
  redirectToPaymentPage
}
